import { useAuth } from '@pooflabs/web';
import { Rocket, Wallet } from 'lucide-react';
import React from 'react';
import TopNav from '@/components/TopNav';
import { RentalBalancePanel } from '@/components/RentalBalancePanel';
import { ActiveRentalCard } from '@/components/ActiveRentalCard';
import { DeployWorkloadForm } from '@/components/DeployWorkloadForm';
import { NetworkStatsStrip } from '@/components/NetworkStatsStrip';
import { EmptyState, PageLayout } from '@/components/poof-ui';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeRentalBalances, type RentalBalancesResponse } from '@/lib/collections/rentalBalances';
import { subscribeManyRentals, type RentalsResponse } from '@/lib/collections/rentals';

/**
 * Nosana-style workload deployment. Renters fund a native SOL escrow
 * balance (rentalBalances), describe a container workload + required GPU
 * tier, and the backend auto-matches it to the best-rated available node
 * from the GPU Network. Accrued SOL is paid to the node owner when the
 * deployment stops. All money-moving actions go through the authenticated
 * backend — the rentals/rentalBalances/rentalPayouts/gpuNodes policies are
 * vault-only.
 */
export const RentGPUsPage: React.FC = () => {
  const { user, login } = useAuth();

  const { data: balance, loading: balanceLoading } = useRealtimeData<RentalBalancesResponse | null>(
    subscribeRentalBalances,
    !!user?.address,
    user?.address ?? ''
  );

  const { data: myActiveRentals } = useRealtimeData<RentalsResponse[]>(
    subscribeManyRentals,
    !!user?.address,
    `renter='${user?.address ?? ''}' limit 5`
  );

  const balanceLamports = balance?.balanceLamports ?? 0;
  const activeRental =
    (myActiveRentals ?? []).find((r) => r.status === 'active' || r.status === 'running') ?? null;

  return (
    <PageLayout background="" fullBleed footer={false}>
      <TopNav />

      <div className="container pb-24 lg:pb-10">
        {/* ── Header ── */}
        <header className="pt-10 pb-2 animate-fade-in-up">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-secondary/70 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-3">
            <Rocket className="w-3 h-3" />
            Auto-matched compute
          </div>
          <h1 className="text-headline font-extrabold tracking-tight">Deploy</h1>
          <p className="text-muted-foreground mt-1.5 max-w-xl">
            Describe your workload and we'll auto-match it to the best-rated available GPU node — no manual picking.
            Fund a SOL balance, deploy, and stop anytime — you only pay for what you use.
          </p>
        </header>

        <div className="mt-6 animate-fade-in-up delay-1">
          <NetworkStatsStrip />
        </div>

        {/* ── Logged-out state ── */}
        {!user && (
          <div className="mt-10 animate-fade-in-up delay-1">
            <EmptyState
              icon={<Wallet />}
              title="Connect your wallet"
              description="Sign in to fund a balance and deploy a workload."
              action={
                <button
                  onClick={() => login()}
                  className="rounded-full bg-[#00C805] text-white font-bold px-6 py-3 hover:bg-[#00b004] transition-colors"
                >
                  Connect wallet
                </button>
              }
            />
          </div>
        )}

        {/* ── Logged-in: balance + active deployment ── */}
        {user && (
          <>
            <div className="grid sm:grid-cols-2 gap-4 mt-8 animate-fade-in-up delay-2">
              <RentalBalancePanel
                walletAddress={user.address}
                balanceLamports={balanceLamports}
                balanceLoading={balanceLoading}
                hasActiveRental={!!activeRental}
              />
              {activeRental ? (
                <ActiveRentalCard
                  rental={activeRental}
                  walletAddress={user.address}
                  balanceLamports={balanceLamports}
                  balanceLoading={balanceLoading}
                />
              ) : (
                <div className="rounded-3xl border border-dashed border-border bg-card/40 p-5 flex flex-col items-center justify-center text-center gap-1.5">
                  <Rocket className="w-5 h-5 text-muted-foreground" />
                  <p className="text-sm font-semibold text-muted-foreground">No active deployment</p>
                  <p className="text-[11px] text-muted-foreground/70">Deploy a workload below to get started.</p>
                </div>
              )}
            </div>

            <section className="mt-10 max-w-2xl animate-fade-in-up delay-3">
              <DeployWorkloadForm
                walletAddress={user.address}
                balanceLamports={balanceLamports}
                hasActiveRental={!!activeRental}
                onDeployed={() => {}}
              />
            </section>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default RentGPUsPage;
